import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './style.module.css';
import GeoEarn from '../GeoEarn';
import { refresh } from '../Carousel';
import api from '../../api';

// --- GEOLOCATION & BEARING HELPERS ---
const toRad = (degrees) => (degrees * Math.PI) / 180;
const toDeg = (radians) => (radians * 180) / Math.PI;

export function distanceBetween(a, b) {
    const radius = 6371000;
    const lat = toRad(b.lat - a.lat);
    const lon = toRad(b.lon - a.lon);
    const value =
        Math.sin(lat / 2) ** 2 +
        Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(lon / 2) ** 2;
    return radius * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}

function bearingBetween(a, b) {
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const lon = toRad(b.lon - a.lon);
    return (
        (toDeg(
            Math.atan2(
                Math.sin(lon) * Math.cos(lat2),
                Math.cos(lat1) * Math.sin(lat2) -
                Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon)
            )
        ) +
            360) %
        360
    );
}

function formatCoordinate(value, positive, negative) {
    return `${Math.abs(value).toFixed(4)}° ${value >= 0 ? positive : negative}`;
}

// --- SUB-COMPONENT: NAVIGATION DISPLAY ---
function NavigationDisplay({ valid, distance, bearing, relativeTurn, initialDistance, targetLabel, showEarn }) {
    const canvasRef = useRef(null);

    // const useKm = distance >= 1000;
    // const distanceDisplay = useKm ? (distance / 1000).toFixed(1) : Math.round(distance);
    // const distanceUnit = useKm ? 'km' : 'm';
    // const headingFormatted = String(Math.round(bearing)).padStart(3, '0');
    // const eta = `${Math.max(1, Math.round(distance / 80))} MIN`;
    // const complete = Math.max(2, Math.min(96, (1 - distance / initialDistance) * 100));

    const useMiles = distance >= 1609.34; // Switch to miles at ~1 mile (1609.34 meters)
    const distanceDisplay = useMiles
        ? (distance / 1609.34).toFixed(1)
        : Math.round(distance * 3.28084);
    const distanceUnit = useMiles ? 'mi' : 'ft';
    const headingFormatted = String(Math.round(bearing)).padStart(3, '0');
    const eta = `${Math.max(1, Math.round(distance / 80))} MIN`;
    const complete = Math.max(2, Math.min(96, (1 - distance / initialDistance) * 100));

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const scale = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = rect.width * scale;
        canvas.height = rect.height * scale;
        const ctx = canvas.getContext('2d');
        ctx.scale(scale, scale);

        const x = rect.width / 2;
        const y = rect.height / 2;

        ctx.clearRect(0, 0, rect.width, rect.height);
        ctx.beginPath();
        ctx.moveTo(x, rect.height * 0.88);
        ctx.bezierCurveTo(
            x - relativeTurn * 1.2,
            y + 130,
            x + relativeTurn * 0.7,
            y - 80,
            x + relativeTurn * 1.8,
            rect.height * 0.12
        );
        ctx.strokeStyle = 'rgba(124,195,197,.08)';
        ctx.lineWidth = 16;
        ctx.stroke();

        ctx.strokeStyle = '#7cc3c5';
        ctx.lineWidth = 2;
        ctx.setLineDash([3, 7]);
        ctx.stroke();
    }, [relativeTurn]);

    const close = valid;

    return (
        <section className={styles.navigation} aria-label="Navigation display">
            <div className={styles.mapField}>
                <canvas ref={canvasRef} className={styles.routeCanvas} />
                <div className={styles.radarRingOne}></div>
                <div className={styles.radarRingTwo}></div>
            </div>

            <div className={styles.compass} aria-label="Compass heading">
                <span className={`${styles.tick} ${styles.west}`}>W</span>
                <span className={`${styles.tick} ${styles.north}`}>N</span>
                <span className={`${styles.tick} ${styles.east}`}>E</span>
                <div className={styles.heading}>
                    <span>{headingFormatted}</span>
                    <small>°</small>
                </div>
            </div>

            <div className={styles.arrowScene}>
                <div className={styles.arrowShadow}></div>
                <div className={styles.navArrow}>
                    {/* Added wrapper for transition-friendly rotation */}
                    <div className={styles.arrowRotator} style={{ '--turn': `${relativeTurn}deg` }}>
                        <div className={styles.arrowCore}></div>
                    </div>
                </div>
                {/* <div className={styles.navArrow} style={{ '--turn': `${relativeTurn}deg` }}>
                    <div className={styles.arrowCore}></div>
                </div> */}
                <div className={styles.pulse}></div>
            </div>

            <div className={styles.instruction}>
                <p>FINDING PASSPORT</p>
                <h1>
                    <span>{distanceDisplay}</span>
                    <small>{distanceUnit}</small>
                </h1>
                <div className={styles.street}>
                    {/* <span>↱</span> */}
                    <strong>{targetLabel}</strong>
                </div>
            </div>
            <div className={styles.journeyProgress} style={{ opacity: !close ? '1.0' : '0.0', filter: !close ? 'blur(0px)' : 'blur(8px)', transition: 'opacity 0.45s ease, filter 0.45s ease' }}>
                <div className={styles.progressMeta}>
                    <span><i></i> YOU</span>
                    <b>{eta}</b>
                    <span style={{ textAlign: 'right' }}>PASSPORT <i></i></span>
                </div>
                <div className={styles.progressTrack}>
                    <span className={styles.progressFill} style={{ width: `${complete}%` }}></span>
                    <i className={styles.progressMarker} style={{ left: `${complete}%` }}></i>
                </div>
            </div>
            <div className={styles.toolbar} style={{ opacity: close ? '1.0' : '0.0', filter: close ? 'blur(0px)' : 'blur(8px)', transition: 'opacity 0.45s ease, filter 0.45s ease' }}>
                <div className={styles.toolbarContent}>
                    <div className={styles.progressButton} onClick={() => showEarn()}>
                        Get My Passport
                    </div>
                </div>
            </div>
        </section>
    );
}

// --- MAIN APPLICATION ENTRY POINT ---
export default function GeoLocation({ id, name, heading, position, destination, onEarn, badge }) {
    // const [position, setPosition] = useState({ lat: 37.7901, lon: -122.3977 });
    // const [target, setTarget] = useState({ lat: 37.7955, lon: -122.3937 });
    // const [heading, setHeading] = useState(28);
    const [initialDistance, setInitialDistance] = useState(640);

    const [panelTitle, setPanelTitle] = useState('Pioneer Park');
    const [renderEarn, setRenderEarn] = useState(false);
    const [showEarn, setShowEarn] = useState(null);
    const [valid, setValid] = useState(false);
    const [log, setlog] = useState('');

    const [permissionReason, setPermissionReason] = useState('');
    const [showPermissionModal, setShowPermissionModal] = useState(false);

    const watchIdRef = useRef(null);
    const compassActiveRef = useRef(false);
    const toastTimerRef = useRef(null);

    const distance = distanceBetween(position, destination);
    const bearing = bearingBetween(position, destination);
    const relativeTurn = ((bearing - heading + 540) % 360) - 180;

    const handleSetDestination = (lat, lon) => {
        // const newTarget = { lat, lon };
        // setTarget(newTarget);
        // setInitialDistance(Math.max(distanceBetween(position, newTarget), 1));
        // setPanelTitle('Custom destination');
        // triggerToast('ROUTE RECALCULATED');
    };

    const check = async () => {
        try {
            const dist = distanceBetween(position, destination) * 3.28084;
            const found = await api.geo.find(id, dist);
            setValid(found.result ? true : false);
            // setlog(JSON.stringify(found));
        } catch (e) {
            // setlog(JSON.stringify(e.response.data))
        }
    };

    useEffect(() => {
        setInterval(() => {
            check();
        }, 1000);
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            const moves = {
                ArrowUp: [0.00018, 0],
                ArrowDown: [-0.00018, 0],
                ArrowLeft: [0, -0.00018],
                ArrowRight: [0, 0.00018],
            };
            if (!moves[e.key]) return;
            e.preventDefault();
            // setPosition((prev) => ({
            //     lat: prev.lat + moves[e.key][0],
            //     lon: prev.lon + moves[e.key][1],
            // }));
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    return (
        <div className={styles.GeoLocation}>
            <main className={styles.mainLayout} style={{ opacity: !showEarn ? '1.0' : '0.0', filter: !showEarn ? 'blur(0px)' : 'blur(8px)', transition: 'opacity 0.45s ease, filter 0.45s ease' }}>
                <NavigationDisplay
                    valid={valid}
                    distance={distance}
                    bearing={bearing}
                    relativeTurn={relativeTurn}
                    initialDistance={initialDistance}
                    targetLabel={name}
                    showEarn={() => { setRenderEarn(true); setShowEarn(true); }}
                />
            </main>
            {showEarn ? <GeoEarn data={badge} onHide={() => {
                setShowEarn(false);
                // onEarn();
                refresh();

                setTimeout(() => {
                    setRenderEarn(false);
                    setShowEarn(false);
                }, 500);
            }} /> : null}
        </div>
    );
}